import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Search, Heart, MessageSquare, User, Plus, Car, Home as HomeIcon, 
  Briefcase, Smartphone, Armchair, Sofa, DollarSign, Dog, Users, 
  MapPin, Star, Eye, Clock, Phone, MessageCircle, Share2, Flag,
  ChevronRight, Filter, X, Globe, Menu, Building, Wrench, Tag,
  Bell, Settings, Shield, FileText, Headphones, LogOut, BadgeCheck, Check,
  ImageIcon, ArrowRight, Flame, Key, Bed, Bath, UserPlus
} from 'lucide-react';
import LanguageSwitcher from './components/LanguageSwitcher';
import NotificationBell from './components/NotificationBell';
import { requestNotificationPermission, onMessageListener } from './firebase';
import PostAdFlow from './pages/PostAdFlow';
import ListingDetail from './pages/ListingDetail';
import { trackPageView } from './analytics';
import AuthPage from './pages/AuthPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SearchResults from './pages/SearchResults';
import UserDashboard from './pages/UserDashboard';
import AdminPanel from './pages/AdminPanel';
import EditListing from './pages/EditListing';
import ChatDetail from './pages/ChatDetail';
import CategorySelectPage from './pages/CategorySelectPage';
import CategoryNavigation from './pages/CategoryNavigation';
import JobsPage from './pages/JobsPage';
import FiltersModal from './pages/FiltersModal';
import SortModal from './pages/SortModal';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PostingRules from './pages/PostingRules';
import SafetyTips from './pages/SafetyTips';
import Advertising from './pages/Advertising';
import './i18n';
import './App.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Also scroll any overflow containers
    document.querySelectorAll('[class*="overflow"]').forEach(el => { el.scrollTop = 0; });
  }, [pathname]);
  return null;
};

// Bottom Navigation Bar Component - Dubizzle Style
const BottomNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const navItems = [
    { path: '/', icon: HomeIcon, labelKey: 'nav.home' },
    { path: '/favorites', icon: Heart, labelKey: 'nav.favorites' },
    { path: '/post-ad', icon: Plus, labelKey: 'nav.placeAd', isCenter: true },
    { path: '/chats', icon: MessageSquare, labelKey: 'nav.chats' },
    { path: '/menu', icon: Menu, labelKey: 'nav.menu' },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[9999] md:hidden" data-testid="bottom-nav">
      <div className="relative bg-[#1A1A2E] h-[68px] flex items-center justify-around px-2 shadow-[0_8px_32px_rgba(0,0,0,0.25)]">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.isCenter ? false : location.pathname === item.path;
          
          if (item.isCenter) {
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="relative -top-4 w-[56px] h-[56px] bg-[#E31C25] rounded-2xl flex items-center justify-center text-white shadow-[0_4px_20px_rgba(227,28,37,0.5)] active:scale-90 transition-transform"
                data-testid="nav-post-ad"
              >
                <Plus className="w-7 h-7" strokeWidth={2.5} />
              </button>
            );
          }
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="flex flex-col items-center justify-center gap-1 w-14 transition-all"
              data-testid={`nav-${t(item.labelKey).toLowerCase().replace(' ', '-')}`}
            >
              <Icon className={`w-[22px] h-[22px] transition-colors ${isActive ? 'text-[#E31C25]' : 'text-white/50'}`} strokeWidth={isActive ? 2 : 1.5} />
              <span className={`text-[10px] font-semibold transition-colors ${isActive ? 'text-[#E31C25]' : 'text-white/40'}`}>{t(item.labelKey)}</span>
              {isActive && <div className="w-1 h-1 bg-[#E31C25] rounded-full" />}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// Desktop Header Component
const DesktopHeader = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <header className="hidden md:block sticky top-0 z-50 bg-white border-b border-gray-200" data-testid="desktop-header">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#E31C25] rounded-lg flex items-center justify-center">
              <Tag className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold font-heading text-gray-900">Findadz</span>
          </Link>

          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <NotificationBell />
            <Link to="/favorites" className="text-gray-600 hover:text-[#E31C25] transition-colors">
              <Heart className="w-6 h-6" strokeWidth={1.5} />
            </Link>
            <Link to="/chats" className="text-gray-600 hover:text-[#E31C25] transition-colors">
              <MessageSquare className="w-6 h-6" strokeWidth={1.5} />
            </Link>
            
            {user ? (
              <div className="flex items-center gap-3">
                <Link to="/menu" className="flex items-center gap-2 text-gray-700 hover:text-[#E31C25]">
                  <User className="w-6 h-6" strokeWidth={1.5} />
                  <span className="font-medium">{user.full_name}</span>
                </Link>
              </div>
            ) : (
              <button 
                onClick={() => navigate('/auth')} 
                className="text-gray-700 hover:text-[#E31C25] font-medium"
              >
                {t('nav.login')}
              </button>
            )}
            
            <button 
              onClick={() => navigate('/post-ad')}
              className="px-5 py-2.5 bg-[#E31C25] text-white rounded-lg font-semibold hover:bg-[#C41820] transition-colors"
              data-testid="desktop-post-ad"
            >
              + {t('nav.placeAd')}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

// Hero Search Component - Modern Dark Hero
const HeroSearch = () => {
  const [query, setQuery] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const placeholderKeys = ['home.property', 'home.cars', 'home.jobs', 'home.furniture', 'home.phones'];

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholderKeys.length);
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const popularSearches = i18n.language === 'ar' 
    ? ['سيارات', 'شقق', 'ايفون', 'وظائف'] 
    : ['Cars', 'Apartments', 'iPhone', 'Jobs'];

  return (
    <div className="hero-gradient px-4 pt-5 pb-10 md:pt-8 md:pb-16 relative" data-testid="hero-section">
      <div className="noise-overlay" />
      <div className="max-w-5xl mx-auto relative z-10">
        {/* Brand */}
        <div className="animate-fade-up mb-5">
          <h2 className="text-lg font-extrabold text-white font-heading tracking-tight">Findadz</h2>
        </div>

        {/* Headline */}
        <div className="animate-fade-up stagger-1 mb-6 md:mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight">
            {i18n.language === 'ar' ? 'اشتري وبيع أي شيء' : 'Buy & Sell'}
            <br />
            <span className="text-[#E31C25]">{i18n.language === 'ar' ? 'في الإمارات' : 'Anything in UAE'}</span>
          </h1>
          <p className="mt-3 text-white/60 text-sm md:text-base max-w-md">
            {i18n.language === 'ar' 
              ? 'أكبر سوق إلكتروني في الإمارات - عقارات، سيارات، وظائف والمزيد'
              : 'The UAE\'s largest marketplace — property, motors, jobs & more'}
          </p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="animate-fade-up stagger-2" data-testid="search-bar">
          <div className="flex items-center gap-3 px-4 py-3 md:px-5 md:py-4 bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.25)]">
            <Search className="w-5 h-5 text-gray-400 flex-shrink-0" strokeWidth={2} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`${t('home.searchPlaceholder')} ${t(placeholderKeys[placeholderIndex])}`}
              className="flex-1 bg-transparent outline-none text-gray-900 placeholder-gray-400 font-body text-sm md:text-base min-w-0"
              data-testid="search-input"
            />
            <button 
              type="submit" 
              className="flex-shrink-0 px-5 py-2 bg-[#E31C25] text-white text-sm font-semibold rounded-xl hover:bg-[#C41820] transition-colors hidden sm:block"
              data-testid="search-submit"
            >
              {i18n.language === 'ar' ? 'بحث' : 'Search'}
            </button>
            <button 
              type="submit" 
              className="flex-shrink-0 w-9 h-9 bg-[#E31C25] rounded-xl flex items-center justify-center sm:hidden"
              data-testid="search-submit-mobile"
            >
              <Search className="w-4 h-4 text-white" strokeWidth={2.5} />
            </button>
          </div>
        </form>

        {/* Popular Searches */}
        <div className="animate-fade-up stagger-3 mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-white/40 text-xs font-medium">{i18n.language === 'ar' ? 'رائج:' : 'Trending:'}</span>
          {popularSearches.map((term) => (
            <button 
              key={term} 
              onClick={() => navigate(`/search?q=${encodeURIComponent(term)}`)}
              className="px-3 py-1 text-xs font-medium text-white/70 bg-white/8 hover:bg-white/15 rounded-full border border-white/10 transition-colors"
              data-testid={`trending-${term.toLowerCase()}`}
            >
              {term}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Category Chip Component
const CategoryChip = ({ icon: Icon, label, slug, onClick, index }) => {
  return (
    <button
      onClick={onClick}
      className={`animate-scale-in stagger-${index + 1} flex-shrink-0 flex items-center gap-2.5 px-4 py-2.5 bg-white rounded-full border border-gray-100 shadow-sm hover:shadow-md hover:border-[#E31C25]/30 hover:bg-red-50 transition-all duration-200 group`}
      data-testid={`category-${slug}`}
    >
      <div className="w-8 h-8 bg-[#FEE2E2] rounded-full flex items-center justify-center group-hover:bg-[#E31C25] transition-colors">
        <Icon className="w-4 h-4 text-[#E31C25] group-hover:text-white transition-colors" strokeWidth={2.5} />
      </div>
      <span className="text-sm font-semibold text-gray-700 whitespace-nowrap group-hover:text-[#E31C25] transition-colors">{label}</span>
    </button>
  );
};

// Category Strip Component - Horizontal Scroll
const CategoryStrip = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showJobsModal, setShowJobsModal] = React.useState(false);
  const isAr = i18n.language === 'ar';

  const categories = [
    { icon: Key, labelKey: 'category.propertyRent', slug: 'property-rent', link: '/browse/property-rent' },
    { icon: HomeIcon, labelKey: 'category.propertySale', slug: 'property-sale', link: '/browse/property-sale' },
    { icon: Building, labelKey: 'category.offPlan', slug: 'off-plan', link: '/browse/off-plan' },
    { icon: Bed, labelKey: 'category.rooms', slug: 'rooms', link: '/browse/rooms' },
    { icon: Car, labelKey: 'category.motors', slug: 'motors', link: '/browse/motors' },
    { icon: Briefcase, labelKey: 'category.jobs', slug: 'jobs', link: '/browse/jobs' },
    { icon: Sofa, labelKey: 'category.classifieds', slug: 'classifieds', link: '/browse/classifieds' },
    { icon: Armchair, labelKey: 'category.furniture', slug: 'furniture', link: '/browse/furniture' },
    { icon: Users, labelKey: 'category.community', slug: 'community', link: '/browse/community' },
  ];

  const handleCategoryClick = (cat) => {
    if (cat.slug === 'jobs') {
      setShowJobsModal(true);
    } else {
      navigate(cat.link);
    }
  };

  return (
    <div data-testid="category-grid">
      {/* Mobile: horizontal scroll */}
      <div className="category-scroll md:hidden px-4">
        {categories.map((cat, i) => (
          <CategoryChip
            key={cat.slug}
            icon={cat.icon}
            label={t(cat.labelKey)}
            slug={cat.slug}
            index={i}
            onClick={() => handleCategoryClick(cat)}
          />
        ))}
      </div>
      {/* Desktop: grid wrap */}
      <div className="hidden md:flex flex-wrap gap-2.5 max-w-5xl mx-auto px-4">
        {categories.map((cat, i) => (
          <CategoryChip
            key={cat.slug}
            icon={cat.icon}
            label={t(cat.labelKey)}
            slug={cat.slug}
            index={i}
            onClick={() => handleCategoryClick(cat)}
          />
        ))}
      </div>

      {/* Jobs Modal */}
      {showJobsModal && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-end justify-center" onClick={() => setShowJobsModal(false)} data-testid="jobs-modal-overlay">
          <div className="w-full max-w-lg bg-gradient-to-b from-[#1A1A2E] to-[#0F3460] rounded-t-3xl overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>
            <div className="px-5 pt-2 pb-28">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-extrabold text-white text-lg tracking-tight">{isAr ? 'الوظائف والتوظيف' : 'Jobs & Hiring'}</h3>
                  <p className="text-white/40 text-xs mt-0.5">{isAr ? 'ماذا تبحث عنه؟' : 'What are you looking for?'}</p>
                </div>
                <button onClick={() => setShowJobsModal(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all" data-testid="close-jobs-modal">
                  <X className="w-4 h-4 text-white/60" />
                </button>
              </div>
              <div className="space-y-3">
                <button onClick={() => { setShowJobsModal(false); navigate('/browse/jobs'); }}
                  className="w-full flex items-center gap-4 p-4 bg-white/[0.05] rounded-2xl border border-white/[0.08] hover:border-[#E31C25]/30 hover:bg-[#E31C25]/5 transition-all active:scale-[0.98] group"
                  data-testid="find-jobs-btn">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E31C25] to-[#ff4757] rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-[#E31C25]/20 group-hover:scale-105 transition-transform">
                    <Search className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-bold text-white text-base">{isAr ? 'البحث عن وظيفة' : 'Find Jobs'}</p>
                    <p className="text-white/40 text-xs mt-0.5">{isAr ? 'احصل على الوظيفة التي تريدها' : 'Get hired at the job you want'}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-[#E31C25] transition-colors" />
                </button>
                <button onClick={() => { setShowJobsModal(false); navigate('/browse/jobs?mode=hire'); }}
                  className="w-full flex items-center gap-4 p-4 bg-white/[0.05] rounded-2xl border border-white/[0.08] hover:border-[#E31C25]/30 hover:bg-[#E31C25]/5 transition-all active:scale-[0.98] group"
                  data-testid="hire-talent-btn">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#6C5CE7] to-[#a29bfe] rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-[#6C5CE7]/20 group-hover:scale-105 transition-transform">
                    <UserPlus className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-bold text-white text-base">{isAr ? 'توظيف' : 'Hire Talent'}</p>
                    <p className="text-white/40 text-xs mt-0.5">{isAr ? 'اعثر على الشخص المناسب للوظيفة' : 'Find the right person for the job'}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-[#6C5CE7] transition-colors" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Quick Action Cards - Modern CTA Section
const QuickActions = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-3 animate-fade-up stagger-4" data-testid="quick-actions">
      <button
        onClick={() => navigate('/post-ad')}
        className="relative overflow-hidden bg-gradient-to-br from-[#E31C25] to-[#b91420] rounded-2xl p-4 md:p-5 text-left group hover:shadow-lg transition-all"
        data-testid="quick-post-ad"
      >
        <div className="absolute -top-4 -right-4 w-20 h-20 bg-white/10 rounded-full" />
        <Plus className="w-8 h-8 text-white/90 mb-2 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
        <p className="text-white font-bold text-sm md:text-base">
          {i18n.language === 'ar' ? 'أضف إعلانك' : 'Post Your Ad'}
        </p>
        <p className="text-white/60 text-xs mt-0.5">
          {i18n.language === 'ar' ? 'مجاناً وسريع' : 'Free & Quick'}
        </p>
      </button>
      <button
        onClick={() => navigate('/browse')}
        className="relative overflow-hidden bg-gradient-to-br from-[#1A1A2E] to-[#16213E] rounded-2xl p-4 md:p-5 text-left group hover:shadow-lg transition-all"
        data-testid="quick-browse"
      >
        <div className="absolute -top-4 -right-4 w-20 h-20 bg-white/5 rounded-full" />
        <Search className="w-8 h-8 text-white/90 mb-2 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
        <p className="text-white font-bold text-sm md:text-base">
          {i18n.language === 'ar' ? 'تصفح الكل' : 'Browse All'}
        </p>
        <p className="text-white/50 text-xs mt-0.5">
          {i18n.language === 'ar' ? 'آلاف الإعلانات' : 'Thousands of Ads'}
        </p>
      </button>
    </div>
  );
};

// Listing Card Component - Dubizzle Style
// Cache favorites list to avoid fetching per card
let _favoritesCache = { ids: [], loaded: false, promise: null };

const loadFavorites = () => {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve([]);
  if (_favoritesCache.loaded) return Promise.resolve(_favoritesCache.ids);
  if (_favoritesCache.promise) return _favoritesCache.promise;
  
  _favoritesCache.promise = axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${token}` } })
    .then(res => {
      _favoritesCache.ids = res.data.map(f => f.listing?.id).filter(Boolean);
      _favoritesCache.loaded = true;
      return _favoritesCache.ids;
    })
    .catch(() => []);
  return _favoritesCache.promise;
};

const invalidateFavoritesCache = () => {
  _favoritesCache = { ids: [], loaded: false, promise: null };
};

const ListingCard = ({ listing, onFavoriteToggle }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    loadFavorites().then(ids => setIsFavorite(ids.includes(listing.id)));
  }, [listing.id]);

  const toggleFavorite = async (e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${listing.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/favorites/${listing.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setIsFavorite(!isFavorite);
      invalidateFavoritesCache();
      if (onFavoriteToggle) onFavoriteToggle(listing.id, !isFavorite);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Format time ago
  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return i18n.language === 'ar' ? `منذ ${diffMins} دقيقة` : `${diffMins} min ago`;
    } else if (diffHours < 24) {
      return i18n.language === 'ar' ? `منذ ${diffHours} ساعة` : `${diffHours} hours ago`;
    } else {
      return i18n.language === 'ar' ? `منذ ${diffDays} يوم` : `${diffDays} days ago`;
    }
  };

  return (
    <div
      onClick={() => navigate(`/listing/${listing.id}`)}
      className="bg-white rounded-2xl overflow-hidden listing-hover border border-gray-100/80 cursor-pointer group"
      data-testid={`listing-${listing.id}`}
    >
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
        {listing.images?.[0] ? (
          <img
            src={listing.images[0].thumbnail_url || listing.images[0].image_url}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
            <ImageIcon className="w-10 h-10 text-gray-200" />
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-2.5 left-2.5 flex gap-1.5">
          {listing.is_featured && (
            <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold rounded-full shadow-sm">
              TURBO
            </span>
          )}
          {listing.is_verified && (
            <span className="px-2 py-0.5 bg-blue-500/90 backdrop-blur-sm text-white text-[10px] font-bold rounded-full flex items-center gap-0.5 shadow-sm">
              <BadgeCheck className="w-3 h-3" />
              {i18n.language === 'ar' ? 'موثق' : 'Verified'}
            </span>
          )}
        </div>
        
        {/* Favorite Button */}
        <button 
          className={`absolute top-2.5 right-2.5 w-8 h-8 rounded-full flex items-center justify-center shadow-sm hover:scale-110 transition-all ${
            isFavorite ? 'bg-[#E31C25] text-white' : 'bg-white/90 backdrop-blur-sm text-gray-400 hover:text-gray-600'
          }`}
          onClick={toggleFavorite}
          data-testid={`favorite-btn-${listing.id}`}
        >
          <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} strokeWidth={2} />
        </button>
        
        {/* Image Counter */}
        {listing.images?.length > 1 && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/50 backdrop-blur-sm rounded-md text-white text-[10px] font-medium flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            {listing.images.length}
          </div>
        )}
      </div>
      
      <div className="p-3">
        <p className="text-[15px] font-extrabold text-[#E31C25] mb-1">
          {t('common.aed')} {new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-AE' : 'en-AE').format(listing.price)}
        </p>
        <h3 className="font-medium text-gray-800 line-clamp-1 text-sm mb-1.5">{listing.title}</h3>
        <div className="flex items-center justify-between text-[11px] text-gray-400">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {listing.location?.city || 'UAE'}
          </span>
          <span>{getTimeAgo(listing.created_at)}</span>
        </div>
      </div>
    </div>
  );
};

// What's New Section Component
const WhatsNewSection = ({ listings }) => {
  const { t, i18n } = useTranslation();
  return (
    <section className="animate-fade-up stagger-5" data-testid="whats-new-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 font-heading">{t('home.whatsNew')}</h2>
          <p className="text-xs text-gray-400 mt-0.5">{i18n.language === 'ar' ? 'أحدث الإعلانات' : 'Recently posted ads'}</p>
        </div>
        <Link to="/search" className="text-xs font-bold text-[#E31C25] hover:underline flex items-center gap-1">
          {t('home.viewAll', 'View All')}
          <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {listings.slice(0, 8).map((listing) => (
          <ListingCard key={listing.id} listing={listing} />
        ))}
      </div>
    </section>
  );
};

// Homepage Component - Modern Redesign
const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView('home');
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/listings?page_size=8`);
        setListings(res.data);
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="pb-24 md:pb-8 bg-[#f7f8fa]" data-testid="homepage">
      {/* Hero Search */}
      <HeroSearch />

      {/* Categories */}
      <div className="py-4 md:py-6 bg-white border-b border-gray-100">
        <CategoryStrip />
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 pt-5">
        {/* Quick Actions */}
        <div className="mb-5">
          <QuickActions />
        </div>

        {/* What's New */}
        {!loading && listings.length > 0 && (
          <WhatsNewSection listings={listings} />
        )}

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-9 h-9 border-[3px] border-[#E31C25] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-12 border-t border-gray-200 pt-8 pb-4" data-testid="footer">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mb-8">
            <div>
              <h3 className="font-bold text-gray-900 text-sm mb-3">{t('footer.company', 'Findadz')}</h3>
              <div className="space-y-2">
                <Link to="/terms" className="block text-sm text-gray-500 hover:text-[#E31C25]">{t('footer.terms', 'Terms of Service')}</Link>
                <Link to="/privacy" className="block text-sm text-gray-500 hover:text-[#E31C25]">{t('footer.privacy', 'Privacy Policy')}</Link>
              </div>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm mb-3">{t('footer.explore', 'Explore')}</h3>
              <div className="space-y-2">
                <Link to="/browse" className="block text-sm text-gray-500 hover:text-[#E31C25]">{t('nav.browse', 'Browse')}</Link>
                <Link to="/search" className="block text-sm text-gray-500 hover:text-[#E31C25]">{t('nav.search', 'Search')}</Link>
                <Link to="/post-ad" className="block text-sm text-gray-500 hover:text-[#E31C25]">{t('nav.postAd', 'Post Ad')}</Link>
              </div>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <h3 className="font-bold text-gray-900 text-sm mb-3">{t('footer.contact', 'Contact')}</h3>
              <div className="space-y-2">
                <p className="text-sm text-gray-500">findadz@hotmail.com</p>
                <p className="text-sm text-gray-500">{t('footer.uae', 'United Arab Emirates')}</p>
              </div>
            </div>
          </div>
          <div className="text-center text-xs text-gray-400 border-t border-gray-100 pt-4">
            &copy; {new Date().getFullYear()} Findadz. {t('footer.rights', 'All rights reserved.')}
          </div>
        </footer>
      </div>
    </div>
  );
};

// Favorites Page Component
const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(res.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (listingId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/favorites/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(favorites.filter(f => f.listing?.id !== listingId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  return (
    <div className="pb-24 md:pb-8 bg-[#f7f8fa]" data-testid="favorites-page">
      {/* Dark Hero Header */}
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-5xl mx-auto px-4 pt-6 pb-8 relative z-10">
          <div className="animate-fade-up">
            <div className="flex items-center gap-2.5 mb-1">
              <h1 className="text-2xl font-extrabold text-white font-heading tracking-tight">{t('favorites.title')}</h1>
              <span className="px-2.5 py-0.5 bg-[#E31C25] text-white text-[10px] font-bold rounded-full">{t('favorites.default')}</span>
            </div>
            <p className="text-white/50 text-sm">{favorites.length} {t('favorites.savedAds')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-9 h-9 border-[3px] border-[#E31C25] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : favorites.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {favorites.map((fav) => (
              <div
                key={fav.id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 listing-hover relative group"
              >
                <button
                  onClick={() => removeFavorite(fav.listing?.id)}
                  className="absolute top-2.5 right-2.5 z-10 w-8 h-8 bg-[#E31C25] rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-fav-${fav.listing?.id}`}
                >
                  <X className="w-4 h-4 text-white" />
                </button>
                <div 
                  onClick={() => navigate(`/listing/${fav.listing?.id}`)}
                  className="cursor-pointer"
                >
                  <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                    {fav.listing?.images?.[0] ? (
                      <img
                        src={fav.listing.images[0].thumbnail_url || fav.listing.images[0].image_url}
                        alt={fav.listing?.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                        <ImageIcon className="w-10 h-10 text-gray-200" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-[15px] font-extrabold text-[#E31C25] mb-1">
                      {t('common.aed')} {new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-AE' : 'en-AE').format(fav.listing?.price || 0)}
                    </p>
                    <h3 className="font-medium text-gray-800 line-clamp-1 text-sm">{fav.listing?.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center mb-5 animate-fade-up">
            <div className="w-16 h-16 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-[#E31C25]" />
            </div>
            <p className="text-gray-500 mb-4 text-sm">{t('favorites.empty')}</p>
            <button 
              onClick={() => navigate('/search')}
              className="px-6 py-2.5 bg-[#E31C25] text-white rounded-xl font-semibold text-sm hover:bg-[#C41820] transition-colors"
              data-testid="browse-listings-btn"
            >
              {t('favorites.browse')}
            </button>
          </div>
        )}

        {/* Personalized Lists */}
        <div className="bg-gradient-to-br from-[#1A1A2E] to-[#16213E] rounded-2xl p-6 text-center relative overflow-hidden animate-fade-up stagger-2" data-testid="personalized-lists">
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/5 rounded-full"></div>
          <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-[#E31C25]/10 rounded-full"></div>
          <div className="relative z-10">
            <h2 className="text-lg font-bold text-white mb-3 font-heading">{t('favorites.personalizedTitle')}</h2>
            <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Heart className="w-6 h-6 text-[#E31C25]" />
            </div>
            <p className="font-semibold text-white text-sm mb-1">{t('favorites.createList')}</p>
            <p className="text-white/50 text-xs mb-1">{t('favorites.organizeDesc')}</p>
            <p className="text-white/50 text-xs mb-4">{t('favorites.inviteDesc')}</p>
            <button className="px-6 py-2.5 bg-white text-[#1A1A2E] rounded-xl font-bold text-sm hover:bg-gray-100 transition-colors" data-testid="make-list-btn">
              {t('favorites.makeList')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Chats Page Component
const ChatsPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const tabs = [
    { id: 'all', labelKey: 'chats.all' },
    { id: 'buying', labelKey: 'chats.buying' },
    { id: 'selling', labelKey: 'chats.selling' },
    { id: 'unread', labelKey: 'chats.unread' },
  ];

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-24 md:pb-8 bg-[#f7f8fa]" data-testid="chats-page">
      {/* Dark Hero Header */}
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-5xl mx-auto px-4 pt-6 pb-5 relative z-10">
          <div className="animate-fade-up">
            <h1 className="text-2xl font-extrabold text-white font-heading tracking-tight">{t('chats.title')}</h1>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto category-scroll">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#E31C25] text-white shadow-sm'
                    : 'bg-white/10 text-white/60 hover:bg-white/15'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-9 h-9 border-[3px] border-[#E31C25] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : conversations.length > 0 ? (
          <div className="space-y-2">
            {conversations.map((conv, index) => (
              <div
                key={conv.id}
                onClick={() => navigate(`/chat/${conv.id}`)}
                className={`animate-fade-up stagger-${Math.min(index + 1, 9)} bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-3 cursor-pointer hover:border-[#E31C25]/20 hover:bg-red-50/20 transition-all`}
                data-testid={`conversation-${conv.id}`}
              >
                <div className="w-12 h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-[#E31C25]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm truncate">{t('chats.title')}</p>
                  <p className="text-xs text-gray-400 truncate mt-0.5">
                    {conv.messages?.[conv.messages.length - 1]?.content || t('chats.noMessages')}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Deactivated Chats */}
            <div className="bg-white rounded-2xl border border-gray-100 mb-4 animate-fade-up">
              <button className="w-full flex items-center justify-between px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <span className="text-gray-400 text-sm font-bold">!</span>
                  </div>
                  <span className="text-gray-600 font-medium text-sm">{t('chats.deactivated')}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </button>
            </div>

            {/* Empty State */}
            <div className="text-center py-12 animate-fade-up stagger-2">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1A1A2E] to-[#16213E] rounded-2xl flex items-center justify-center mx-auto mb-5 relative">
                <MessageSquare className="w-10 h-10 text-white/80" />
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#E31C25] rounded-full border-2 border-[#f7f8fa]"></div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2 font-heading">{t('chats.empty')}</h2>
              <p className="text-gray-400 text-sm mb-6 max-w-xs mx-auto">{t('chats.emptyDesc')}</p>
              <button 
                onClick={() => navigate('/search')}
                className="px-8 py-3 bg-[#E31C25] text-white rounded-xl font-bold text-sm hover:bg-[#C41820] transition-colors mb-3"
                data-testid="explore-btn"
              >
                {t('chats.explore')}
              </button>
              <div>
                <button 
                  onClick={() => navigate('/post-ad')}
                  className="text-gray-400 font-medium text-sm hover:text-[#E31C25] transition-colors"
                  data-testid="post-ad-link"
                >
                  {t('chats.postAd')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Menu Page Component
const MenuPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState(t('menu.allCities'));

  const cities = [
    { key: 'allCities', en: 'All Cities', ar: 'جميع المدن' },
    { key: 'dubai', en: 'Dubai', ar: 'دبي' },
    { key: 'abuDhabi', en: 'Abu Dhabi', ar: 'أبوظبي' },
    { key: 'sharjah', en: 'Sharjah', ar: 'الشارقة' },
    { key: 'ajman', en: 'Ajman', ar: 'عجمان' },
    { key: 'rasAlKhaimah', en: 'Ras Al Khaimah', ar: 'رأس الخيمة' },
    { key: 'fujairah', en: 'Fujairah', ar: 'الفجيرة' },
    { key: 'ummAlQuwain', en: 'Umm Al Quwain', ar: 'أم القيوين' }
  ];

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    setShowLanguageModal(false);
  };

  const handleCityChange = (city) => {
    setSelectedCity(i18n.language === 'ar' ? city.ar : city.en);
    setShowCityModal(false);
  };

  // Reusable menu row
  const MenuRow = ({ icon: Icon, label, onClick, value, isLast, testId }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-3.5 ${!isLast ? 'border-b border-gray-50' : ''} hover:bg-red-50/30 transition-colors`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
        <span className="text-gray-800 font-medium text-sm">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {value && <span className="text-xs text-gray-400">{value}</span>}
        <ChevronRight className="w-4 h-4 text-gray-300" />
      </div>
    </button>
  );

  return (
    <div className="pb-24 md:pb-8 bg-[#f7f8fa]" data-testid="menu-page">
      {/* Language Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 z-[100] bg-black/50" onClick={() => setShowLanguageModal(false)}>
          <div className="absolute bottom-0 left-0 right-0 bg-[#f7f8fa] rounded-t-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="hero-gradient relative">
              <div className="noise-overlay" />
              <div className="px-4 py-4 relative z-10">
                <h3 className="font-bold text-white text-base text-center font-heading">{t('menu.language')}</h3>
              </div>
            </div>
            <div className="p-3 space-y-1.5">
              <button onClick={() => handleLanguageChange('en')}
                className={`w-full px-4 py-3.5 rounded-2xl flex items-center justify-between transition-all ${i18n.language === 'en' ? 'bg-[#E31C25] text-white' : 'bg-white border border-gray-100 text-gray-700'}`}>
                <span className="font-medium text-sm">{t('menu.english')}</span>
                {i18n.language === 'en' && <Check className="w-4 h-4" />}
              </button>
              <button onClick={() => handleLanguageChange('ar')}
                className={`w-full px-4 py-3.5 rounded-2xl flex items-center justify-between transition-all ${i18n.language === 'ar' ? 'bg-[#E31C25] text-white' : 'bg-white border border-gray-100 text-gray-700'}`}>
                <span className="font-medium text-sm">{t('menu.arabic')}</span>
                {i18n.language === 'ar' && <Check className="w-4 h-4" />}
              </button>
            </div>
            <div className="p-3 pt-0">
              <button onClick={() => setShowLanguageModal(false)} className="w-full py-3 bg-white rounded-2xl border border-gray-100 font-semibold text-sm text-gray-600">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* City Modal */}
      {showCityModal && (
        <div className="fixed inset-0 z-[100] bg-black/50" onClick={() => setShowCityModal(false)}>
          <div className="absolute bottom-0 left-0 right-0 bg-[#f7f8fa] rounded-t-2xl overflow-hidden max-h-[70vh]" onClick={e => e.stopPropagation()}>
            <div className="hero-gradient relative">
              <div className="noise-overlay" />
              <div className="px-4 py-4 relative z-10">
                <h3 className="font-bold text-white text-base text-center font-heading">{t('menu.city')}</h3>
              </div>
            </div>
            <div className="p-3 space-y-1.5 overflow-y-auto max-h-[calc(70vh-120px)]">
              {cities.map(city => {
                const isSelected = selectedCity === (i18n.language === 'ar' ? city.ar : city.en);
                return (
                  <button key={city.key} onClick={() => handleCityChange(city)}
                    className={`w-full px-4 py-3.5 rounded-2xl flex items-center justify-between transition-all ${isSelected ? 'bg-[#E31C25] text-white' : 'bg-white border border-gray-100 text-gray-700'}`}>
                    <span className="font-medium text-sm">{i18n.language === 'ar' ? city.ar : city.en}</span>
                    {isSelected && <Check className="w-4 h-4" />}
                  </button>
                );
              })}
            </div>
            <div className="p-3 pt-0">
              <button onClick={() => setShowCityModal(false)} className="w-full py-3 bg-white rounded-2xl border border-gray-100 font-semibold text-sm text-gray-600">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dark Hero Header with Profile */}
      <div className="hero-gradient relative">
        <div className="noise-overlay" />
        <div className="max-w-5xl mx-auto px-4 pt-6 pb-8 relative z-10">
          <div className="flex items-center gap-4 animate-fade-up">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center border-2 border-white/20">
              <User className="w-8 h-8 text-white/80" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white font-heading">{user?.full_name || t('common.guest')}</h2>
              {user ? (
                <p className="text-white/50 text-sm mt-0.5">
                  {t('menu.joined')} {new Date(user.created_at || Date.now()).toLocaleDateString(i18n.language === 'ar' ? 'ar-AE' : 'en-US', { month: 'long', year: 'numeric' })}
                </p>
              ) : (
                <button onClick={() => navigate('/auth')} className="text-[#E31C25] font-semibold text-sm mt-0.5">
                  {t('menu.loginRegister')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Admin Panel Button */}
        {user?.is_admin && (
          <button 
            onClick={() => navigate('/admin')}
            className="w-full bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-2xl p-4 mb-3 flex items-center justify-between shadow-sm animate-fade-up"
            data-testid="admin-panel-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <span className="font-semibold text-sm">{t('admin.title')}</span>
            </div>
            <ChevronRight className="w-5 h-5 text-white/70" />
          </button>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mb-3 animate-fade-up stagger-1">
          <button 
            onClick={() => user ? navigate('/dashboard') : navigate('/auth')}
            className="bg-white rounded-2xl p-4 border border-gray-100 flex flex-col items-center gap-2.5 hover:shadow-md hover:border-[#E31C25]/20 transition-all group"
            data-testid="my-ads-btn"
          >
            <div className="w-11 h-11 bg-[#FEE2E2] rounded-xl flex items-center justify-center group-hover:bg-[#E31C25] transition-colors">
              <FileText className="w-5 h-5 text-[#E31C25] group-hover:text-white transition-colors" strokeWidth={2} />
            </div>
            <span className="font-bold text-gray-800 text-sm">{t('menu.myAds')}</span>
          </button>
          <button 
            onClick={() => navigate('/search')}
            className="bg-white rounded-2xl p-4 border border-gray-100 flex flex-col items-center gap-2.5 hover:shadow-md hover:border-[#E31C25]/20 transition-all group" 
            data-testid="my-searches-btn"
          >
            <div className="w-11 h-11 bg-[#FEE2E2] rounded-xl flex items-center justify-center group-hover:bg-[#E31C25] transition-colors">
              <Search className="w-5 h-5 text-[#E31C25] group-hover:text-white transition-colors" strokeWidth={2} />
            </div>
            <span className="font-bold text-gray-800 text-sm">{t('menu.mySearches')}</span>
          </button>
        </div>

        {/* Account Section */}
        <div className="bg-white rounded-2xl border border-gray-100 mb-3 overflow-hidden animate-fade-up stagger-2">
          <MenuRow icon={User} label={t('menu.profile')} onClick={() => user ? navigate('/dashboard?tab=profile') : navigate('/auth')} testId="menu-profile" />
          <MenuRow icon={Settings} label={t('menu.accountSettings')} onClick={() => user ? navigate('/dashboard?tab=settings') : navigate('/auth')} testId="menu-account-settings" />
          <MenuRow icon={Bell} label={t('menu.notificationSettings')} onClick={() => user ? navigate('/dashboard?tab=settings&section=notifications') : navigate('/auth')} testId="menu-notifications" />
          <MenuRow icon={Shield} label={t('menu.security')} onClick={() => user ? navigate('/dashboard?tab=settings&section=security') : navigate('/auth')} isLast testId="menu-security" />
        </div>

        {/* Preferences Section */}
        <div className="bg-white rounded-2xl border border-gray-100 mb-3 overflow-hidden animate-fade-up stagger-4">
          <MenuRow icon={Building} label={t('menu.city')} value={selectedCity} onClick={() => setShowCityModal(true)} testId="menu-city" />
          <MenuRow icon={Globe} label={t('menu.language')} value={i18n.language === 'ar' ? t('menu.arabic') : t('menu.english')} onClick={() => setShowLanguageModal(true)} isLast testId="menu-language" />
        </div>

        {/* Support Section */}
        <div className="bg-white rounded-2xl border border-gray-100 mb-3 overflow-hidden animate-fade-up stagger-5">
          <MenuRow icon={Headphones} label={t('menu.support')} value="findadz@hotmail.com" onClick={() => window.open('mailto:findadz@hotmail.com?subject=Findadz Support')} testId="menu-support" />
          <MenuRow icon={Tag} label={t('menu.advertising')} onClick={() => navigate('/advertising')} isLast testId="menu-advertising" />
        </div>

        {/* Legal Hub */}
        <div className="bg-white rounded-2xl border border-gray-100 mb-3 overflow-hidden animate-fade-up stagger-6">
          <MenuRow icon={FileText} label={t('menu.termsOfService')} onClick={() => navigate('/terms')} testId="menu-terms" />
          <MenuRow icon={Shield} label={t('menu.privacyPolicy')} onClick={() => navigate('/privacy')} testId="menu-privacy" />
          <MenuRow icon={FileText} label={t('menu.postingRules')} onClick={() => navigate('/posting-rules')} testId="menu-posting-rules" />
          <MenuRow icon={Shield} label={t('menu.safetyTips')} onClick={() => navigate('/safety-tips')} isLast testId="menu-safety-tips" />
        </div>

        {/* Logout */}
        {user && (
          <button
            onClick={onLogout}
            className="w-full bg-white rounded-2xl border border-gray-100 p-4 flex items-center justify-center gap-2.5 text-[#E31C25] font-bold text-sm mb-3 hover:bg-red-50 transition-colors animate-fade-up stagger-6"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>{t('menu.logout')}</span>
          </button>
        )}

        <p className="text-center text-gray-300 text-xs pb-4">{t('menu.version')}</p>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const { i18n } = useTranslation();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
  }, []);

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Hide Emergent platform badge on mobile to prevent nav bar overlap
  useEffect(() => {
    const hideBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge && window.innerWidth <= 768) {
        badge.style.setProperty('display', 'none', 'important');
      }
    };
    hideBadge();
    const observer = new MutationObserver(hideBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', hideBadge);
    return () => { observer.disconnect(); window.removeEventListener('resize', hideBadge); };
  }, []);

  // Register for push notifications when user is logged in
  useEffect(() => {
    if (!user) return;
    const registerPush = async () => {
      try {
        // Register service worker
        if ('serviceWorker' in navigator) {
          await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        }
        const fcmToken = await requestNotificationPermission();
        if (fcmToken) {
          const authToken = localStorage.getItem('token');
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/register-token`, 
            { token: fcmToken },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
        }
      } catch (err) {
        console.log('Push notification setup skipped:', err.message);
      }
    };
    registerPush();

    // Listen for foreground messages
    onMessageListener().then((payload) => {
      if (payload?.notification) {
        new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: '/favicon.ico',
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/';
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="min-h-screen bg-[#F8F9FA] font-body" style={{ backgroundColor: '#F8F9FA' }}>
        <DesktopHeader user={user} onLogout={handleLogout} />
        
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/post-ad" element={<PostAdFlow />} />
          <Route path="/auth" element={<AuthPage onLogin={handleLogin} />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/category/:slug" element={<SearchResults />} />
          <Route path="/category-select" element={<CategorySelectPage />} />
          <Route path="/category-select/:categorySlug" element={<CategorySelectPage />} />
          <Route path="/browse" element={<CategoryNavigation />} />
          <Route path="/browse/:categoryId" element={<CategoryNavigation />} />
          <Route path="/browse/:categoryId/:subcategoryId" element={<CategoryNavigation />} />
          <Route path="/browse/:categoryId/:subcategoryId/:subSubcategoryId" element={<CategoryNavigation />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/listing/:id" element={<ListingDetail />} />
          <Route path="/dashboard" element={<UserDashboard />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/chats" element={<ChatsPage />} />
          <Route path="/chat/:conversationId" element={<ChatDetail />} />
          <Route path="/edit-listing/:id" element={<EditListing />} />
          <Route path="/menu" element={<MenuPage user={user} onLogout={handleLogout} />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/posting-rules" element={<PostingRules />} />
          <Route path="/safety-tips" element={<SafetyTips />} />
          <Route path="/advertising" element={<Advertising />} />
        </Routes>

        <BottomNavBar />
      </div>
    </BrowserRouter>
  );
}

export default App;
